import { z } from 'zod';
import {
  EMAIL_DIRECTIONS,
  EMAIL_SOURCES,
  EMAIL_STATUSES,
  SUPPRESSION_REASONS,
  type EmailDirection,
  type EmailEventType,
  type EmailSource,
  type EmailStatus,
  type SendAttemptOutcome,
  type SuppressionReason,
} from '../constants';
import type { IsoDateTime } from './common';

/** Comma-separated (or repeated) query values → array of allowed enum values. */
const csvEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v),
    z.array(z.enum(values)).max(values.length),
  );

const Subject = z.string().max(500);
const Body = z.string().max(50_000);

/** Live preview of a 1:1 email from the composer (nothing is stored). */
export const EmailPreviewBody = z.object({
  contactId: z.uuid().nullish(),
  toEmail: z.string().trim().max(320).nullish(),
  templateId: z.uuid().nullish(),
  subject: Subject.optional(),
  body: Body.optional(),
  /** The mailbox picked in the composer (see SendEmailBody); omitted = chosen automatically, like the send. */
  emailAccountId: z.uuid().nullish(),
});
export type EmailPreviewBody = z.infer<typeof EmailPreviewBody>;

/**
 * Send (or schedule) a 1:1 email. `clientRequestId` is generated once per composer session; the server derives
 * the idempotency key `manual:<clientRequestId>` so double clicks and retries can never send twice.
 */
export const SendEmailBody = z.object({
  contactId: z.uuid().nullish(),
  toEmail: z.string().trim().max(320).nullish(),
  templateId: z.uuid().nullish(),
  subject: Subject.refine((s) => s.trim() !== '', 'Subject is required'),
  body: Body.refine((s) => s.trim() !== '', 'Write a message'),
  scheduledFor: z.iso.datetime({ offset: true }).nullish(),
  clientRequestId: z.uuid(),
  /** Send from this connected mailbox (one of the sender's own); omitted = their default, else the organization sender. */
  emailAccountId: z.uuid().nullish(),
});
export type SendEmailBody = z.infer<typeof SendEmailBody>;

export const EMAIL_LIST_SORTS = ['recent', 'scheduled'] as const;

export const EmailListQuery = z.object({
  businessId: z.uuid().optional(),
  status: csvEnum(EMAIL_STATUSES).optional(),
  source: csvEnum(EMAIL_SOURCES).optional(),
  direction: z.enum(EMAIL_DIRECTIONS).optional(),
  campaignId: z.uuid().optional(),
  /** Inbound messages not linked to any lead (the "Unmatched replies" queue). */
  unmatched: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  /** Only rows created/sent within the last N days (e.g. "Needs attention" shows recent failures). */
  sinceDays: z.coerce.number().int().min(1).max(3650).optional(),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(EMAIL_LIST_SORTS).default('recent'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type EmailListQuery = z.input<typeof EmailListQuery>;

export const CancelEmailBody = z.object({ reason: z.string().trim().max(500).optional() });
export const RejectEmailBody = z.object({ reason: z.string().trim().max(500).optional() });
export const BulkEmailIdsBody = z.object({
  ids: z.array(z.uuid()).min(1).max(200),
  reason: z.string().trim().max(500).optional(),
});
export const ResolveUncertainBody = z.object({
  outcome: z.enum(['sent', 'not_sent']),
  note: z.string().trim().max(1000).optional(),
});
export const AssignInboundBody = z.object({ businessId: z.uuid(), contactId: z.uuid().nullish() });

export type EmailPreviewDTO = {
  composed: {
    subject: string;
    text: string;
    html: string;
    from: { email: string; name: string | null };
    replyTo: string | null;
    toEmail: string;
    toName: string | null;
  };
  missing: string[];
  unknown: string[];
  eligibility: { eligible: boolean; reasons: EligibilityReasonDTO[] };
  /**
   * Not blocking: set when the email would not reach the recipient — none of the sender's mailboxes sends it and the
   * organization sender only records mail (the log provider) or is not set up at all.
   */
  deliveryWarning: string | null;
};

export type EligibilityReasonDTO = { code: string; message: string; blocking: boolean };

/** A possible recipient of a 1:1 email for one lead, with its current outreach state. */
export type EmailRecipientDTO = {
  contactId: string | null;
  kind: 'contact' | 'business';
  name: string;
  role: string | null;
  email: string | null;
  isPrimary: boolean;
  eligible: boolean;
  reasons: EligibilityReasonDTO[];
};

export type EmailListItemDTO = {
  id: string;
  businessId: string | null;
  businessName: string | null;
  contactId: string | null;
  contactName: string | null;
  direction: EmailDirection;
  status: EmailStatus;
  source: EmailSource;
  subject: string;
  /** First ~160 characters of the plain-text body. */
  snippet: string;
  fromEmail: string;
  fromName: string | null;
  toEmail: string;
  toName: string | null;
  scheduledFor: IsoDateTime | null;
  sentAt: IsoDateTime | null;
  deliveredAt: IsoDateTime | null;
  bouncedAt: IsoDateTime | null;
  bounceType: 'hard' | 'soft' | null;
  repliedAt: IsoDateTime | null;
  receivedAt: IsoDateTime | null;
  failureReason: string | null;
  cancelledReason: string | null;
  attemptCount: number;
  campaignId: string | null;
  enrollmentId: string | null;
  templateId: string | null;
  createdAt: IsoDateTime;
  createdBy: string | null;
};

export type EmailAttemptDTO = {
  id: string;
  attemptNo: number;
  provider: string;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
  outcome: SendAttemptOutcome | null;
  providerResponse: string | null;
  error: string | null;
};

export type EmailEventDTO = {
  id: string;
  type: EmailEventType;
  occurredAt: IsoDateTime;
  provider: string;
  detail: string | null;
};

export type EmailDetailDTO = EmailListItemDTO & {
  bodyText: string;
  bodyHtml: string | null;
  replyTo: string | null;
  provider: string | null;
  providerMessageId: string | null;
  messageIdHeader: string | null;
  inReplyToId: string | null;
  bounceReason: string | null;
  approvedBy: string | null;
  approvedAt: IsoDateTime | null;
  cancelledAt: IsoDateTime | null;
  lastAttemptAt: IsoDateTime | null;
  sequenceStepId: string | null;
  /** Headers as sent (outbound) — From, To, Reply-To, Message-ID, List-Unsubscribe… */
  headers: { name: string; value: string }[];
  attempts: EmailAttemptDTO[];
  events: EmailEventDTO[];
  /** Replies received to this message (outbound) or the message this replies to (inbound). */
  related: EmailListItemDTO[];
};

export type SendEmailResponse = {
  email: EmailDetailDTO;
  /** false when the same clientRequestId was already used (no second email was created). */
  created: boolean;
  /** Non-blocking eligibility warnings (e.g. frequency cap) the sender accepted. */
  warnings: EligibilityReasonDTO[];
  /** Variables that rendered empty. */
  missing: string[];
};

/** Counts behind the Email page tabs and the sidebar badge. */
export type EmailCountsDTO = {
  outbox: number;
  approval: number;
  needsAttention: number;
  unmatchedReplies: number;
};

export type BulkEmailActionResult = {
  done: string[];
  skipped: { id: string; reason: string }[];
};

// ── Suppressions ─────────────────────────────────────────────────────────────────────────────────────

export const SUPPRESSION_LIST_STATUSES = ['active', 'lifted', 'all'] as const;

export const SuppressionListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  reason: csvEnum(SUPPRESSION_REASONS).optional(),
  status: z.enum(SUPPRESSION_LIST_STATUSES).default('active'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type SuppressionListQuery = z.input<typeof SuppressionListQuery>;

export const CreateSuppressionBody = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address').max(320),
  reason: z.enum(SUPPRESSION_REASONS).default('manual'),
  notes: z.string().trim().max(1000).optional(),
});
export type CreateSuppressionBody = z.input<typeof CreateSuppressionBody>;

export const LiftSuppressionBody = z.object({
  reason: z.string().trim().min(3, 'Explain why the address may be emailed again').max(1000),
});
export type LiftSuppressionBody = z.infer<typeof LiftSuppressionBody>;

export type SuppressionDTO = {
  id: string;
  email: string;
  reason: SuppressionReason;
  source: string;
  businessId: string | null;
  businessName: string | null;
  contactId: string | null;
  contactName: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: IsoDateTime;
  liftedAt: IsoDateTime | null;
  liftedBy: string | null;
  liftReason: string | null;
};
